import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import { outputIfNoVariables, debug } from '../utilities/misc';
import { rem } from '../utilities/style';
import { colors } from '../theme/colors';
import { headers, body, richTextStyles } from '../styles/type';

import Button from './buttons/Button';
import ButtonGroup from './ButtonGroup';
import ButtonText from './buttons/ButtonText';
import TextRenderer from './TextRenderer';
import Image from './Image';

const Card = styled.div`
  display: flex;
  flex-direction: column;
  ${({ minHeight }) => (minHeight && `
    min-height: ${rem(400)};
  `)};
  height: 100%;
  box-sizing: border-box;
  padding: ${rem(36)} ${rem(32)};
  border-radius: ${rem(12)};

  ${({ featured }) => (featured ? `
    background: ${colors.blue['800']};
    color: ${colors.gray['200']};
  ` : `
    background: ${colors.white};
    color: ${colors.gray['600']};
    box-shadow: 0 ${rem(2)} ${rem(8)} rgba(0, 0, 0, 0.08),
                0 ${rem(8)} ${rem(40)} rgba(0, 0, 0, 0.08);
  `)};

  ${({ outlined, featured }) => (outlined && !featured && `
    border: 1px solid ${colors.gray['200']};
    background-color: transparent;
    box-shadow: none;
  `)};

  ${({ clickable }) => clickable && `
    cursor: pointer;
  `}

  ${ButtonGroup} {
    margin-top: auto;
  }
`;

const Icon = styled(Image)`
  border-radius: ${rem(8)};
  margin-bottom: ${rem(32)};
  width: ${rem(80)};
  height: ${rem(80)};
`;

const Subhead = styled.p`
  ${headers.eyebrow}
  font-size: ${rem(14)};
  margin-bottom: ${rem(12)};
`;

const Headline = styled.h3`
  ${headers.h4}
  color: ${({ featured }) => (featured ? colors.white : colors.blue['800'])};
`;

const Text = styled.div`
  ${body.regular}
  ${richTextStyles}
  margin-top: ${rem(20)};
  margin-bottom: ${rem(20)};
`;

const TextCard = ({
  icon,
  subhead,
  headline,
  text,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  featured = false,
  minHeight = true,
  outlined = false,
  clickable
}) => {
  // Log the props to see their values
  debug('clickable:', clickable);
  debug('featured:', featured);

  const primaryButtonRef = useRef(null);

  const handleCardClick = () => {
    if (clickable && primaryButtonRef.current) {
      primaryButtonRef.current.click();
    }
  };

  return (
    <Card
      featured={featured}
      minHeight={minHeight}
      clickable={clickable}
      outlined={outlined}
      onClick={handleCardClick}
    >
      {icon && (
        <Icon image={icon} objectFit="cover" />
      )}
      {subhead && (
        <Subhead>
          <TextRenderer text={subhead} />
        </Subhead>
      )}
      <Headline featured={featured}>
        <TextRenderer text={headline} />
      </Headline>
      <Text light={featured}>
        <TextRenderer text={text} richText />
      </Text>
      <ButtonGroup>
        {primaryCtaText && primaryCtaLink && (
          <Button
            ref={primaryButtonRef}
            to={primaryCtaLink}
            aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
          >
            <TextRenderer text={primaryCtaText} />
          </Button>
        )}
        {secondaryCtaText && secondaryCtaLink && (
          <ButtonText
            to={secondaryCtaLink}
            color={featured ? 'light' : 'dark'}
            aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
            onClick={(event) => event.stopPropagation()}
          >
            <TextRenderer text={secondaryCtaText} />
          </ButtonText>
        )}
      </ButtonGroup>
    </Card>
  );
};

TextCard.propTypes = {
  subhead: PropTypes.string,
  headline: PropTypes.string,
  text: PropTypes.shape({
    raw: PropTypes.string
  }),
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  featured: PropTypes.bool,
  minHeight: PropTypes.bool,
  clickable: PropTypes.bool
};

export default TextCard;
